import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send, Sparkles, Loader2 } from "lucide-react";

const TRANSPORT = [
  ["ambulance", "Ambulance (with resuscitation kit)"],
  ["private", "Private vehicle"],
  ["self", "Self-arranged"],
];

// Referral-form keys the AI can auto-fill (backend returns these + the 8 shared fields).
const AI_FIELDS = [
  "final_diagnosis",
  "medications_on_discharge",
  "referral_reason",
  "referral_current_condition",
  "referral_stabilization",
];

export default function ReferralForm({ f, updateField, patientId }) {
  const [drafting, setDrafting] = useState(false);
  const runAi = async () => {
    if (!patientId) return;
    setDrafting(true);
    try {
      const { data } = await api.post(`/ai/referral-fields/${patientId}`);
      const draft = data.draft || {};
      AI_FIELDS.forEach((k) => {
        if (draft[k]) updateField(k, draft[k]);
      });
      toast.success("Referral fields auto-filled — review before printing");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI referral fill failed"); }
    finally { setDrafting(false); }
  };
  return (
    <div className="card-clinical p-5 border-l-4 border-l-amber-500" data-testid="referral-form">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl grid place-items-center bg-amber-500/15 text-amber-700 dark:text-amber-300">
            <Send size={18} />
          </div>
          <div>
            <div className="font-medium">Refer to Higher Centre</div>
            <div className="text-xs text-muted-foreground">All fields feed the printable referral slip.</div>
          </div>
        </div>
        <Button variant="outline" size="sm" className="rounded-full border-amber-500/40 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10"
                onClick={runAi} disabled={drafting} data-testid="ref-ai-fill">
          {drafting ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Sparkles size={12} className="mr-1" />}
          {drafting ? "Drafting…" : "AI · Auto-fill referral"}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Destination facility">
          <Input value={f.referral_facility || ""} onChange={(e) => updateField("referral_facility", e.target.value)} data-testid="ref-facility"
                 placeholder="e.g., AIIMS NICU / Apollo Children's Hospital" />
        </Field>

        <Field label="Contact number">
          <Input value={f.referral_contact || ""} onChange={(e) => updateField("referral_contact", e.target.value)} data-testid="ref-contact" />
        </Field>

        <Field label="Facility address" full>
          <Textarea rows={2} value={f.referral_address || ""} onChange={(e) => updateField("referral_address", e.target.value)} data-testid="ref-address" />
        </Field>

        <Field label="Reason for referral" full>
          <Textarea rows={3} value={f.referral_reason || ""} onChange={(e) => updateField("referral_reason", e.target.value)} data-testid="ref-reason"
                    placeholder="e.g., requires cardiac surgery / advanced ventilation not available here…" />
        </Field>

        <Field label="Working diagnosis" full>
          <Textarea rows={2} value={f.final_diagnosis || ""} onChange={(e) => updateField("final_diagnosis", e.target.value)} data-testid="ref-diagnosis" />
        </Field>

        <Field label="Current clinical condition" full>
          <Textarea rows={3} value={f.referral_current_condition || ""} onChange={(e) => updateField("referral_current_condition", e.target.value)} data-testid="ref-condition"
                    placeholder="Vitals, respiratory status, IV access, ongoing infusions…" />
        </Field>

        <Field label="Stabilization / treatment given prior to transfer" full>
          <Textarea rows={3} value={f.referral_stabilization || ""} onChange={(e) => updateField("referral_stabilization", e.target.value)} data-testid="ref-stabilization"
                    placeholder="e.g., intubated, on inotropes, IV antibiotics started…" />
        </Field>

        <Field label="Ongoing medications / infusions" full>
          <Textarea rows={2} value={f.medications_on_discharge || ""} onChange={(e) => updateField("medications_on_discharge", e.target.value)} data-testid="ref-meds" />
        </Field>

        <Field label="Mode of transport">
          <select
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={f.referral_mode_of_transport || ""} onChange={(e) => updateField("referral_mode_of_transport", e.target.value)}
            data-testid="ref-transport"
          >
            <option value="">Select…</option>
            {TRANSPORT.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>

        <Field label="Attendant / escort">
          <Input value={f.referral_attendant || ""} onChange={(e) => updateField("referral_attendant", e.target.value)} data-testid="ref-attendant"
                 placeholder="e.g., Dr X + Nurse Y" />
        </Field>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
